import { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { pauseListeningToday } from '../../services/availability.service';
import { useActiveCohortSessionsStore } from '../../stores/active-cohort-sessions.store';
import type { AvailabilityNotice } from '../../types/ring';
import { Z_BANNER } from '../../utils/layers';
import { useQuietAppNotices } from '../notices/quietZone';
import { alreadySeen, markSeen } from './noticeSeen';

/** User-facing copy (D56: never "on call"; no dashes). The notice text itself is the server's. */
export const NOTICE_COPY = {
  pause: 'Pause for today',
  paused: "Paused for today. You won't be rung again today.",
  dismiss: 'Dismiss',
} as const;

/**
 * Today's "You're available" notice as a toast (P7d, D51): the push's twin
 * for a listener without push (web), shown ONCE per notice per device. Its
 * one action is the push card's: Pause for today.
 */
export default function AvailabilityNoticeToast({ notice }: { notice: AvailabilityNotice }) {
  const [visible, setVisible] = useState(() => !alreadySeen(notice.key));
  const [state, setState] = useState<'idle' | 'pausing' | 'paused'>('idle');
  // The circle-starting notice yields to this toast while it is up (N9).
  useQuietAppNotices(visible);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) markSeen(notice.key);
  }, [visible, notice.key]);

  if (!visible) return null;

  const pause = async () => {
    setState('pausing');
    setError(null);
    const outcome = await pauseListeningToday();
    if (outcome.success) {
      setState('paused');
      void useActiveCohortSessionsStore.getState().refresh();
      return;
    }
    setState('idle');
    setError(outcome.message);
  };

  return (
    <div
      className="fixed inset-x-0 top-[max(1rem,env(safe-area-inset-top),calc(var(--ee-top-notice-bottom,0px)+var(--ee-space-2)))] flex justify-center px-4 pointer-events-none"
      style={{ zIndex: Z_BANNER }}
    >
      <section
        role="status"
        aria-live="polite"
        data-testid="availability-notice"
        className="pointer-events-auto w-full max-w-md rounded-2xl border border-success/30 bg-base-100 p-4 shadow-lg flex gap-3"
      >
        <div className="flex-1 flex flex-col gap-1.5">
          {state === 'paused' ? (
            <p className="m-0 text-sm font-semibold">{NOTICE_COPY.paused}</p>
          ) : (
            <>
              <p className="m-0 text-[15px] font-semibold leading-snug">{notice.title}</p>
              <p className="m-0 text-sm text-(--ee-tw-gray-600) leading-snug">{notice.body}</p>
              {error ? <p className="m-0 text-sm text-error">{error}</p> : null}
              <div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline mt-1 min-h-11"
                  onClick={() => void pause()}
                  disabled={state === 'pausing'}
                >
                  {NOTICE_COPY.pause}
                </button>
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm btn-square min-h-11 min-w-11"
          aria-label={NOTICE_COPY.dismiss}
          onClick={() => setVisible(false)}
        >
          <FiX className="w-4 h-4" aria-hidden="true" />
        </button>
      </section>
    </div>
  );
}
